import type ConversationReadingPaneViewState from "owa-mail-reading-pane-store/lib/store/schema/ConversationReadingPaneViewState";
import { getStore } from "owa-mail-reading-pane-store/lib/store/Store";
import { mutatorAction } from "satcheljs";
import { secondaryTabsHaveId } from "owa-tab-store";
import getPrimaryTabId from "owa-mail-reading-pane-store/lib/utils/getPrimaryTabId";
import type FocusedItemPart from "owa-mail-reading-pane-store/lib/store/schema/FocusedItemPart";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { lazyCompressConversation } from "owa-mail-body-compression";

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const addLoadedConversationReadingPaneViewState: (conversationReadingPaneViewState: ConversationReadingPaneViewState) => void = mutatorAction("addLoadedConversationReadingPaneViewState", function (conversationReadingPaneViewState: ConversationReadingPaneViewState): void {
    const conversationId = conversationReadingPaneViewState.conversationId.Id;
    const { loadedConversationReadingPaneViewStates } = getStore();
    if (!loadedConversationReadingPaneViewStates.has(conversationId)) {
        loadedConversationReadingPaneViewStates.set(conversationId, conversationReadingPaneViewState);
    }
});

const releaseOrphanedPrintPaneViewStatesMutator: () => void = mutatorAction("releaseOrphanedLoadedConversationViewStates", function (): void {
    const { itemPrintPaneViewStates } = getStore();
    
    // Release any unused view states from item print pane
    itemPrintPaneViewStates &&
        itemPrintPaneViewStates.forEach((_viewState, tabId) => {
            const itemId = _viewState?.itemId;
            if (itemId && !(getPrimaryTabId() == itemId || secondaryTabsHaveId(itemId))) {
                itemPrintPaneViewStates.delete(tabId);
            }
        });
});

export function releaseOrphanedLoadedConversationViewStates(): void {
    // Collect orphaned IDs before the mutation removes them from the store
    const orphanedIds: string[] = [];
    
    const { loadedConversationReadingPaneViewStates } = getStore();
    loadedConversationReadingPaneViewStates.forEach((_viewState, loadedConversationId) => {
        if (!secondaryTabsHaveId(loadedConversationId) &&
            loadedConversationId != getPrimaryTabId()) {
            orphanedIds.push(loadedConversationId);
        }
    });
    
    for (const id of orphanedIds) {
        releaseLoadedConversationViewStateMutator(id);
        
        // If the feature is enabled, compress the conversation to save memory
        if (isGlobalFeatureEnabled("mon-mail-cold-body-compression")) {
            lazyCompressConversation.importAndExecute(id);
        }
    }
    
    releaseOrphanedPrintPaneViewStatesMutator();
}

const releaseLoadedConversationViewStateMutator: (conversationId: string) => void = mutatorAction("releaseLoadedConversationViewState", function (conversationId: string): void {
    getStore().loadedConversationReadingPaneViewStates.delete(conversationId);
});

export function releaseLoadedConversationViewState(conversationId: string): void {
    releaseLoadedConversationViewStateMutator(conversationId);
    
    // If the feature is enabled, compress the conversation to save memory
    if (isGlobalFeatureEnabled("mon-mail-cold-body-compression")) {
        lazyCompressConversation.importAndExecute(conversationId);
    }
}

export const updateLoadingStateMutator: (conversationViewState: ConversationReadingPaneViewState, isLoading: boolean, hasLoadFailed: boolean) => void = mutatorAction("updateLoadingStateMutator", (conversationViewState: ConversationReadingPaneViewState, isLoading: boolean, hasLoadFailed: boolean): void => {
    conversationViewState.loadingState.isLoading = isLoading;
    conversationViewState.loadingState.hasLoadFailed = hasLoadFailed;
});

export const updateUnsupportedItemIdMutator: (conversationViewState: ConversationReadingPaneViewState, unsupportedItemId: string) => void = mutatorAction("updateUnsupportedItemIdMutator", (conversationViewState: ConversationReadingPaneViewState, unsupportedItemId: string): void => {
    conversationViewState.unsupportedItemId = unsupportedItemId;
});

export const updateCurrentSelectedItemPartMutator: (conversationViewState: ConversationReadingPaneViewState, initiallySelectedItemPart: FocusedItemPart, focusedItemPart: FocusedItemPart) => void = mutatorAction("updateCurrentSelectedItemPartMutator", (conversationViewState: ConversationReadingPaneViewState, initiallySelectedItemPart: FocusedItemPart, focusedItemPart: FocusedItemPart): void => {
    conversationViewState.initiallySelectedItemPart = initiallySelectedItemPart;
    conversationViewState.focusedItemPart = focusedItemPart;
});

export const updateCopilotInboxScoreMutator: (conversationViewState: ConversationReadingPaneViewState, copilotInboxScore: number) => void = mutatorAction("updateCopilotInboxScoreMutator", (conversationViewState: ConversationReadingPaneViewState, copilotInboxScore: number): void => {
    conversationViewState.conversationCopilotInboxScore = copilotInboxScore;
});
